import {
    Badge,
    Button,
    CircularProgress,
    Divider,
    useTheme,
    Typography,
    Box,
    Chip,
} from '@mui/material'
import { FlexDiv } from '../../components'
import { ControlPoint, ExpandLess, ExpandMore, Assignment } from '@mui/icons-material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lease } from '../../types/lease'
import { ModelMap } from '../../types'
import { InventoryConfig } from '../../types/aptConfig'
import { useAppDispatch } from '../../hooks'
import {
    setAreaInspection,
    setInventoryConfig,
    setInventoryInspection,
} from '../../store/Inspection/action'
import { AnimationItem, EnqueueAnimation } from '../../hooks/types'

interface Props {
    lease: Lease
    loading: boolean
    updateLease: (lease: Lease) => void
    currentAnimation?: AnimationItem
    enqueueAnimation: EnqueueAnimation
    onStatusChange: () => void
}

export const AreaContainer = (props: Props) => {
    const { lease, currentAnimation } = props
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [expand, setExpand] = useState(false)

    const [invConfigMap, setInvConfigMap] = useState<ModelMap<InventoryConfig>>(
        { readyForUse: false },
    )

    useEffect(() => {
        const newInvConfigMap: ModelMap<InventoryConfig> = {}
        lease.tenant_move_in_inspection?.inventory_configs.forEach(
            (inventoryConfig) => {
                newInvConfigMap[inventoryConfig.id] = inventoryConfig
            },
        )
        newInvConfigMap.readyForUse = true
        setInvConfigMap(newInvConfigMap)
    }, [])

    const issueCount =
        lease.tenant_move_in_inspection?.inventory_inspection.reduce(
            (prev, inv_ins) => {
                return prev + inv_ins.issue.length
            },
            0,
        ) ?? 0

    const isComplete = lease.tenant_move_in_inspection?.status === 'COMPLETE'

    let completeButton = null

    if (!isComplete) {
        if (props.loading) {
            completeButton = (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="caption" style={{ color: '#8b949e' }}>
                        Completing...
                    </Typography>
                </Box>
            )
        } else {
            completeButton = (
                <Button
                    variant="contained"
                    onClick={() => {
                        setExpand(false)
                        props.onStatusChange()
                    }}
                    sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 2,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                        }
                    }}
                >
                    Complete Area
                </Button>
            )
        }
    }

    return (
        <Box
            sx={{
                background: 'linear-gradient(135deg, #2a2f35 0%, #1e2328 100%)',
                borderRadius: 3,
                padding: 3,
                marginBottom: 2,
                border: `1px solid ${theme.palette.grey[700]}`,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                position: 'relative',
                overflow: 'hidden',
                ...currentAnimation?.effectMap[lease.id]?.animationStyles,
            }}
            className={currentAnimation?.effectMap[lease.id]?.animationCssClass}
        >
            {/* Status accent bar */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    backgroundColor: isComplete ? theme.palette.success.main : theme.palette.primary.main,
                }}
            />

            {/* Header */}
            <FlexDiv
                style={{
                    alignItems: 'center',
                    marginBottom: theme.spacing(2),
                }}
            >
                <FlexDiv
                    direction="column"
                    style={{
                        alignItems: 'flex-start',
                        flex: 1,
                    }}
                >
                    <FlexDiv style={{ alignItems: 'center', marginBottom: theme.spacing(1) }}>
                        <Assignment
                            style={{
                                color: isComplete ? theme.palette.success.main : theme.palette.primary.main,
                                marginRight: theme.spacing(1),
                                fontSize: 20
                            }}
                        />
                        <Typography
                            variant="h6"
                            style={{
                                color: '#fff',
                                fontWeight: 600,
                            }}
                        >
                            {lease.area.area_config_name} {lease.area.area_label}
                        </Typography>
                    </FlexDiv>

                    <FlexDiv style={{ alignItems: 'center' }}>
                        <Chip
                            label={isComplete ? 'Complete' : 'In Progress'}
                            size="small"
                            sx={{
                                backgroundColor: isComplete ? theme.palette.success.main : theme.palette.warning.main,
                                color: 'white',
                                fontWeight: 600,
                                marginRight: theme.spacing(2),
                            }}
                        />
                        <Typography
                            variant="caption"
                            style={{ color: '#8b949e' }}
                        >
                            {issueCount} issue{issueCount !== 1 ? 's' : ''}
                        </Typography>
                    </FlexDiv>
                </FlexDiv>

                {completeButton}
            </FlexDiv>

            {/* Expandable Content */}
            {expand && (
                <Box sx={{ marginTop: theme.spacing(2) }}>
                    <Divider sx={{ backgroundColor: theme.palette.grey[600], marginBottom: theme.spacing(2) }} />

                    <Typography
                        variant="subtitle2"
                        style={{
                            color: '#fff',
                            marginBottom: theme.spacing(2),
                            fontWeight: 600,
                        }}
                    >
                        Inventory Items
                    </Typography>

                    {lease.tenant_move_in_inspection !== undefined &&
                        lease.tenant_move_in_inspection.inventory_inspection.map(
                            (inventoryInspection) => {
                                return (
                                    <Box
                                        key={`INVENTORY_CONFIG_${inventoryInspection.id}_AREA_${lease.area.id}_${lease.id}`}
                                        sx={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            borderRadius: 2,
                                            padding: 2,
                                            marginBottom: 1,
                                            border: `1px solid ${theme.palette.grey[600]}`,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                borderColor: theme.palette.primary.main,
                                            }
                                        }}
                                        onClick={() => {
                                            dispatch(
                                                setAreaInspection(
                                                    lease.tenant_move_in_inspection,
                                                ),
                                            )
                                            dispatch(
                                                setInventoryConfig(
                                                    invConfigMap[
                                                        inventoryInspection
                                                            .inventory_config
                                                    ],
                                                ),
                                            )
                                            dispatch(
                                                setInventoryInspection(
                                                    inventoryInspection,
                                                ),
                                            )
                                            navigate('../issue/')
                                        }}
                                    >
                                        <FlexDiv style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                            <FlexDiv style={{ alignItems: 'center' }}>
                                                {invConfigMap.readyForUse && (
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            color: '#fff',
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {
                                                            invConfigMap[
                                                                inventoryInspection
                                                                    .inventory_config
                                                            ]?.name
                                                        }
                                                    </Typography>
                                                )}
                                            </FlexDiv>

                                            <Badge
                                                badgeContent={
                                                    inventoryInspection.issue
                                                        .length
                                                }
                                                color="primary"
                                                sx={{
                                                    '& .MuiBadge-badge': {
                                                        backgroundColor: theme.palette.primary.main,
                                                        color: 'white',
                                                        fontWeight: 600,
                                                    }
                                                }}
                                            >
                                                <ControlPoint
                                                    style={{
                                                        color: theme.palette.primary.main,
                                                        fontSize: 24
                                                    }}
                                                />
                                            </Badge>
                                        </FlexDiv>
                                    </Box>
                                )
                            },
                        )}
                </Box>
            )}

            {/* Expand/Collapse Button */}
            <Box
                sx={{
                    marginTop: theme.spacing(2),
                    textAlign: 'center',
                }}
            >
                <Button
                    variant="text"
                    onClick={() => {
                        setExpand(!expand)
                    }}
                    endIcon={expand ? <ExpandLess /> : <ExpandMore />}
                    sx={{
                        color: theme.palette.primary.main,
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                            backgroundColor: 'rgba(0, 140, 133, 0.1)',
                        }
                    }}
                >
                    {expand ? 'Hide Details' : isComplete ? 'View Details' : 'Add Issues'}
                </Button>
            </Box>
        </Box>
    )
}
